import { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dark Navy / Gold severity palette
const SEVERITY_STYLES = {
  info:    { bg: "bg-[#0A192F]",     text: "text-[#C5A059]",  border: "border-[#C5A059]/30" },
  warning: { bg: "bg-amber-600",     text: "text-white",      border: "border-amber-700" },
  urgent:  { bg: "bg-rose-700",      text: "text-white",      border: "border-rose-800" },
};

/**
 * Storefront-wide announcement ribbon. Polls /api/announcements/active so
 * scheduled launches and expiries take effect within ~60 seconds without
 * a hard refresh. Dismissals are remembered per-announcement (by id) in
 * localStorage so a buyer doesn't see the same ribbon every page load.
 */
export default function AnnouncementBar() {
  const [active, setActive] = useState([]);

  useEffect(() => {
    let alive = true;
    const fetchActive = async () => {
      try {
        const { data } = await axios.get(`${API}/announcements/active`);
        if (alive) setActive(data?.announcements || []);
      } catch {
        if (alive) setActive([]);
      }
    };
    fetchActive();
    const t = setInterval(fetchActive, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!active.length) return null;

  // Show newest first that hasn't been dismissed.
  const dismissed = new Set(
    (() => { try { return JSON.parse(localStorage.getItem("aigl_dismissed_ann") || "[]"); } catch { return []; } })()
  );
  const visible = active.filter((a) => !dismissed.has(a.id));
  if (!visible.length) return null;

  const ann = visible[0];
  const style = SEVERITY_STYLES[ann.severity] || SEVERITY_STYLES.info;

  const dismiss = () => {
    try {
      const next = [...dismissed, ann.id];
      localStorage.setItem("aigl_dismissed_ann", JSON.stringify(next));
      setActive((prev) => prev.filter((a) => a.id !== ann.id));
    } catch (e) {
      // noop — dismissal best-effort
      void e;
    }
  };

  return (
    <div
      data-testid="announcement-bar"
      className={`w-full ${style.bg} ${style.text} border-b ${style.border} relative z-30`}
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex-1 text-center font-accent text-xs tracking-wider">
          {ann.message}
        </div>
        <button
          data-testid="announcement-dismiss-btn"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className={`shrink-0 p-1 ${style.text} opacity-70 hover:opacity-100`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
